/**
 * Centralized currency formatting with comma thousands separators.
 * Format: "1,234 DKK" (currency after amount)
 */

const DEFAULT_LOCALE = 'en-US';
const DEFAULT_CURRENCY = 'DKK';
const DEFAULT_DECIMALS = 0;

/**
 * Format a number as localized currency (da-DK). Currency code appears after the amount.
 * @param {number|string} value - The monetary amount
 * @param {object} options - Formatting options
 * @param {string} options.currency - Currency code (default: 'DKK')
 * @param {number} options.decimals - Number of decimal places (default: 0)
 * @param {boolean} options.compact - Use compact notation for large numbers (default: false)
 * @param {string} options.locale - Locale string (default: 'da-DK')
 * @returns {string} Formatted currency string e.g. "1,234 DKK" or "1.2K DKK"
 */
export function formatMoney(value, options = {}) {
  const {
    currency = DEFAULT_CURRENCY,
    decimals = DEFAULT_DECIMALS,
    compact = false,
    locale = DEFAULT_LOCALE,
  } = options;

  if (value === null || value === undefined) return '—';

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(numValue)) return '—';

  try {
    const formatOptions = {
      style: 'decimal',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    };
    if (compact) {
      formatOptions.notation = 'compact';
      formatOptions.compactDisplay = 'short';
    }

    const formatted = new Intl.NumberFormat(locale, formatOptions).format(numValue);
    return `${formatted} ${currency}`;
  } catch (err) {
    return `${new Intl.NumberFormat(DEFAULT_LOCALE, {
      style: 'decimal',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(numValue)} ${currency}`;
  }
}

/**
 * Format a number as a simple integer with thousand separators (da-DK locale).
 * Useful for invoice counts, units sold, etc.
 * @param {number|string} value - The number
 * @param {object} options - Formatting options
 * @param {number} options.decimals - Number of decimal places (default: 0)
 * @param {boolean} options.compact - Use compact notation (default: false)
 * @param {string} options.locale - Locale string (default: 'da-DK')
 * @returns {string} Formatted number string e.g. "1,234" or "1.2K"
 */
export function formatNumber(value, options = {}) {
  const { decimals = 0, compact = false, locale = DEFAULT_LOCALE } = options;

  if (value === null || value === undefined) return '—';

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(numValue)) return '—';

  try {
    const formatOptions = {
      style: 'decimal',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    };
    if (compact) {
      formatOptions.notation = 'compact';
      formatOptions.compactDisplay = 'short';
    }

    return new Intl.NumberFormat(locale, formatOptions).format(numValue);
  } catch (err) {
    return new Intl.NumberFormat(DEFAULT_LOCALE, {
      style: 'decimal',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(numValue);
  }
}

/**
 * Format a percentage value with optional sign and specified decimals.
 * @param {number|string} value - The percentage value (e.g., 12.34 for 12.34%)
 * @param {object} options - Formatting options
 * @param {number} options.decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted percentage e.g. "+12.34%" or "-5.60%"
 */
export function formatPercent(value, options = {}) {
  const { decimals = 2 } = options;

  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  const sign = numValue > 0 ? '+' : (numValue < 0 ? '-' : '');
  const locale = DEFAULT_LOCALE;

  try {
    const formatted = new Intl.NumberFormat(locale, {
      style: 'decimal',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(Math.abs(numValue));

    return `${sign}${formatted}%`;
  } catch (err) {
    return `${sign}${numValue.toFixed(decimals)}%`;
  }
}
